import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import ResizeHandle from '../ui/ResizeHandle';

/**
 * SplitPaneLayout - Non-blocking split-pane container
 * 
 * KAI Standard: 100vw/100vh section, embedded module pattern
 * DEX Standard: Premium minimalism, 700ms transitions
 * 
 * The left panel PUSHES content (not overlays), is resizable, and pinnable.
 */

// Context for split pane state
const SplitPaneContext = createContext(null);

export function useSplitPane() {
  const context = useContext(SplitPaneContext);
  if (!context) {
    throw new Error('useSplitPane must be used within SplitPaneLayout');
  }
  return context;
}

// Storage key for persisting panel width
const PANEL_WIDTH_KEY = 'eq-refine-panel-width';
const PANEL_PINNED_KEY = 'eq-refine-panel-pinned';

export default function SplitPaneLayout({ 
  children,
  leftPanel,
  defaultWidth = 420,
  minWidth = 320,
  maxWidth = 600,
}) {
  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem(PANEL_PINNED_KEY);
    return saved === 'true';
  });
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : defaultWidth;
  });
  
  // Persist width preference
  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, panelWidth.toString());
  }, [panelWidth]);
  
  // Persist pinned state
  useEffect(() => {
    localStorage.setItem(PANEL_PINNED_KEY, isPinned.toString());
  }, [isPinned]);
  
  // Handle resize with constraints
  const handleResize = useCallback((delta) => {
    setPanelWidth(prev => {
      const newWidth = prev + delta;
      return Math.max(minWidth, Math.min(maxWidth, newWidth));
    });
  }, [minWidth, maxWidth]);
  
  // Open panel
  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);
  
  // Close panel (only if not pinned)
  const closePanel = useCallback(() => {
    if (!isPinned) {
      setIsOpen(false);
    }
  }, [isPinned]);
  
  // Toggle pin state
  const togglePin = useCallback(() => {
    setIsPinned(prev => !prev);
  }, []);
  
  // Context value
  const contextValue = {
    isOpen: isOpen || isPinned,
    isPinned,
    panelWidth,
    openPanel,
    closePanel,
    togglePin,
  };
  
  const showPanel = isOpen || isPinned;
  
  return (
    <SplitPaneContext.Provider value={contextValue}>
      <div 
        className="flex overflow-hidden" 
        style={{ 
          position: 'fixed',
          left: 'var(--sidebar-width, 64px)',
          top: '56px',
          right: 0,
          bottom: 0,
          width: 'auto',
          maxWidth: 'none',
          transition: 'left 300ms ease-out'
        }}
      >
        {/* Left Panel - Refine Co-Pilot */}
        <div
          className={`
            flex-shrink-0 bg-white border-r border-black/10 
            relative overflow-hidden h-full
            transition-all duration-700 ease-out
          `}
          style={{ 
            width: showPanel ? panelWidth : 0,
            opacity: showPanel ? 1 : 0,
          }}
        >
          {showPanel && (
            <>
              {/* Panel Content */}
              <div className="h-full overflow-hidden">
                {leftPanel}
              </div>
              
              {/* Resize Handle */}
              <ResizeHandle 
                onResize={handleResize}
                minWidth={minWidth}
                maxWidth={maxWidth}
                position="right"
              />
            </>
          )}
        </div>
        
        {/* Right Content Area - Adjusts to remaining width */}
        <div 
          className="flex-1 overflow-hidden transition-all duration-700 ease-out h-full"
          style={{ 
            marginLeft: 0,
          }}
        >
          {children}
        </div>
      </div>
    </SplitPaneContext.Provider>
  );
}

/**
 * SplitPaneToggle - Button to toggle the split pane open/closed
 * Use this in the main content area to open the refine panel
 */
export function SplitPaneToggle({ className = '' }) {
  const { isOpen, openPanel } = useSplitPane();
  
  if (isOpen) return null;
  
  return (
    <button
      onClick={openPanel}
      className={`
        px-4 py-2 text-sm font-medium 
        text-[#5a20ff] bg-[#5a20ff]/10 
        rounded-xl hover:bg-[#5a20ff]/20 
        transition-all duration-200
        hover:scale-105 active:scale-95
        ${className}
      `}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      Refine Results
    </button>
  );
}
